<template>
  <div class="blackout-period-form flex-grow-1 d-flex flex-column overflow-y-hidden">
    <a-form id="blackout-form" class="flex-grow-1 d-flex flex-column overflow-y-hidden" layout="vertical" :form="blackoutPeriodForm" @submit="onSubmit">
      <div class="a-header">
        <span class="a-header--title slide-down-enter-active">
          {{ startDate && endDate ? 'Edit' : 'Add' }}
          Blackout Period
        </span>
      </div>
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
        <Spinner />
      </div>
      <div class="a-body p-20-px flex-grow-1 overflow-y-auto">
        <transition name="slide-up">
          <a-row v-if="!isLoading" :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <template slot="label">
                  Select the URL(s) for the blackout period
                  <a-tooltip placement="top">
                    <template slot="title">
                      <span class="fs-10">
                        During the blackout period, these URLs will not be monitored
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>

                <div class="url-selection">
                  <div class="url-selection__actions mb-3">
                    <a-row type="flex" :gutter="12" align="middle">
                      <a-col :span="8">
                        <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                          <a-button size="large">
                            <a-icon type="search" />
                          </a-button>
                          <a-input class="custom-prefix" placeholder="Search URLs" @input="onSearch" />
                        </div>
                      </a-col>
                      <a-col v-if="isAddMode" :span="8">
                        <div
                          class="full-w a-select-group url-selection__status-select"
                          :class="{ active: isBlackoutStatusFilterActive }"
                        >
                          <a-button size="large">
                            <a-icon type="filter" />
                          </a-button>
                          <a-select
                            v-model="filterParams.blackoutStatuses"
                            mode="multiple"
                            size="large"
                            placeholder="Blackout status"
                            :disabled="isLoadingBlackoutStatuses"
                            :max-tag-count="2"
                            :max-tag-text-length="14"
                            option-filter-prop="label"
                            @change="onBlackoutStatusFilterChange"
                          >
                            <a-select-option
                              v-for="opt in BlackoutUrlStatusFilterOptions"
                              :key="opt.value"
                              :value="opt.value"
                              :label="opt.label"
                            >
                              <a-tag class="status" :class="getBlackoutStatusTagClass(opt.value)">
                                {{ opt.label }}
                              </a-tag>
                            </a-select-option>
                          </a-select>
                        </div>
                      </a-col>
                    </a-row>
                    <a-row type="flex" :gutter="12" align="middle" class="url-selection__toolbar-row">
                      <a-col>
                        <a-space>
                          <a-button
                            v-if="isStatusCodeMonitoringFeatureEnabled"
                            size="default"
                            :type="isShowing404Urls ? 'primary' : 'default'"
                            :loading="isLoadingUrls"
                            @click="toggle404Urls"
                          >
                            <a-icon type="filter" />
                            {{ isShowing404Urls ? 'Show All URLs' : 'Show 404 URLs' }}
                          </a-button>
                          <a-button size="default" @click="selectAll">
                            <a-icon type="check-square" />
                            Select All
                          </a-button>
                          <a-button size="default" :disabled="!isHasSelected" @click="deselectAll">
                            <a-icon type="close-square" />
                            Deselect All
                          </a-button>
                          <a-button type="primary" size="default" @click="showBatchSelectUrlsModal">
                            <a-icon type="upload" />
                            Batch Select URLs
                          </a-button>
                        </a-space>
                      </a-col>
                    </a-row>
                  </div>

                  <div class="url-selection__summary mb-3">
                    <a-alert
                      :message="selectionSummaryMessage"
                      :type="selectedRowKeys.length > 0 ? 'success' : 'info'"
                      show-icon
                    />
                    <div v-if="selectedRowKeys.length > 0" class="selected-tags-container mt-2">
                      <a-tag
                        v-for="key in selectedRowKeys.slice(0, visibleTagCount)"
                        :key="key"
                        closable
                        @close="removeSelectedUrl(key)"
                      >
                        {{ getUrlByKey(key) }}
                      </a-tag>
                      <a-tag v-if="selectedRowKeys.length > visibleTagCount" @click="showAllTags = !showAllTags">
                        {{ showAllTags ? 'Show less' : `+${selectedRowKeys.length - visibleTagCount} more` }}
                      </a-tag>
                      <template v-if="showAllTags">
                        <a-tag
                          v-for="key in selectedRowKeys.slice(visibleTagCount)"
                          :key="'extra-' + key"
                          closable
                          @close="removeSelectedUrl(key)"
                        >
                          {{ getUrlByKey(key) }}
                        </a-tag>
                      </template>
                    </div>
                  </div>

                  <div ref="urlTableWrapper" v-resize="elSizeChange">
                    <PaginationTable
                      :pagination="pagin"
                      :columns="urlColumns"
                      :dataSource="filteredData"
                      :tableSize="urlTableSize"
                      :hasSelectRow="true"
                      @changeSelectedRow="onSelectChange"
                    >
                      <template slot="url" slot-scope="{ record }">
                        <span class="text-wrap">{{ record.title }}</span>
                      </template>
                    </PaginationTable>
                  </div>
                  <div class="a-footer mt-2">
                    <PaginationBar
                      storageKey="blackoutUrlPageSize"
                      :pageSize.sync="pagin.pageSize"
                      :total="pagin.total"
                      @change="paginChange"
                    />
                  </div>

                  <a-modal
                    :closable="false"
                    title="Batch Select URLs"
                    width="45vw"
                    :visible="isShowBatchSelectUrlsModal"
                    @cancel="closeBatchSelectUrlsModal"
                    @ok="handleSubmitBatchSelectUrls"
                  >
                    <p class="mb-2">Paste URLs below (one per line). Matching URLs will be selected.</p>
                    <a-textarea
                      :placeholder="`Paste URLs to select (one URL per line):\neg. http://www.example.com/index.html\nhttps://www.example.com/about.html\nhttp://www.example.com/contact.html`"
                      :autosize="{ minRows: 12, maxRows: 14 }"
                      :value="batchSelectUrls"
                      @change="handleChangeBatchSelectUrls"
                    />
                  </a-modal>
                </div>
              </a-form-item>
            </a-col>
            <a-col v-if="!disableRepeating" :span="24">
              <a-form-item label="Schedule Type">
                <a-radio-group v-model="mode" size="large">
                  <a-radio-button v-for="m in BlackoutModes" :key="m.value" :value="m.value">
                    {{ m.label }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>
            </a-col>

            <template v-if="mode === BLACKOUT_MODE.ONCE">
              <a-col :span="6">
              <a-form-item label="Blackout Start Date/Time" has-feedback>
                  <a-date-picker
                    v-decorator="[
                      'start',
                      {
                        rules: [
                          {
                            required: true,
                            message: 'Please select start date.'
                          }
                        ],
                        getValueFromEvent: (value) => (
                          value ? value.clone().seconds(0).milliseconds(0) : value
                        ),
                      }
                    ]"
                    class="full-w"
                  placeholder="Select Blackout Start"
                    :disabledDate="disabledStartDate"
                    :disabledTime="disabledStartTime"
                    :showTime="{ format: 'HH:mm' }"
                    :format="DATE_FORMAT.DDMMMYYYYHHmm"
                    @change="onChangeStartDate"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="6">
              <a-form-item label="Blackout End Date/Time" has-feedback>
                  <a-date-picker
                    v-decorator="[
                      'end',
                      {
                        rules: [
                          {
                            required: true,
                            message: 'Please select end date.'
                          }
                        ],
                        getValueFromEvent: (value) => (
                          value ? value.clone().seconds(0).milliseconds(0) : value
                        ),
                      }
                    ]"
                    class="full-w"
                  placeholder="Select Blackout End"
                    :disabledDate="disabledEndDate"
                    :disabledTime="disabledEndTime"
                    :showTime="{ format: 'HH:mm' }"
                    :format="DATE_FORMAT.DDMMMYYYYHHmm"
                    @change="onChangeEndDate"
                  />
                </a-form-item>
              </a-col>
            </template>

            <template v-else>
              <a-col :span="6">
                <a-form-item label="Effective From" has-feedback>
                  <a-date-picker
                    v-model="effectiveFrom"
                    class="full-w"
                    placeholder="Start date"
                    :disabled-date="disabledEffectiveFrom"
                    :format="DATE_FORMAT.DDMMMYYYY"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="6">
                <a-form-item label="Effective To" has-feedback>
                  <a-date-picker
                    v-model="effectiveTo"
                    class="full-w"
                    placeholder="End date"
                    :disabled-date="disabledEffectiveTo"
                    :format="DATE_FORMAT.DDMMMYYYY"
                  />
                </a-form-item>
              </a-col>

              <a-col :span="24">
                <a-form-item label="Frequency">
                  <a-radio-group v-model="frequency" size="large">
                    <a-radio-button v-for="f in BlackoutFrequencies" :key="f.value" :value="f.value">
                      {{ f.label }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
              </a-col>

              <template v-if="frequency === BLACKOUT_FREQUENCY.DAILY">
                <a-col :span="24">
                  <p class="text-muted mb-2 fs-12">
                    Each day, blackout runs from the start time through the end time. If the end time is earlier than the start time, it continues until that time on the following day (up to 24 hours).
                  </p>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="Start Time" has-feedback>
                    <a-time-picker
                      v-model="recStartTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="Start time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="End Time" has-feedback>
                    <a-time-picker
                      v-model="recEndTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="End time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
              </template>

              <template v-if="frequency === BLACKOUT_FREQUENCY.WEEKLY">
                <a-col :span="24">
                  <p class="text-muted mb-2 fs-12">
                    Each week, blackout runs from the start day and time through the end day and time (up to 7 days per week).
                  </p>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="Start Day" has-feedback>
                    <a-select v-model="recStartDayOfWeek" class="full-w">
                      <a-select-option v-for="d in DaysOfWeek" :key="'ws-' + d.value" :value="d.value">
                        {{ d.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="Start Time" has-feedback>
                    <a-time-picker
                      v-model="recStartTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="Start time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="End Day" has-feedback>
                    <a-select v-model="recEndDayOfWeek" class="full-w">
                      <a-select-option v-for="d in DaysOfWeek" :key="'we-' + d.value" :value="d.value">
                        {{ d.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="End Time" has-feedback>
                    <a-time-picker
                      v-model="recEndTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="End time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
              </template>

              <template v-if="frequency === BLACKOUT_FREQUENCY.MONTHLY">
                <a-col :span="24">
                  <p class="text-muted mb-2 fs-12">
                    Each month, blackout runs from the start date and time through the end date and time (up to 31 days; shorter months clamp days 29–31).
                  </p>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="Start Date (day)" has-feedback>
                    <a-input-number v-model="recStartDayOfMonth" class="full-w" :min="1" :max="31" :precision="0" />
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="Start Time" has-feedback>
                    <a-time-picker
                      v-model="recStartTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="Start time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="End Date (day)" has-feedback>
                    <a-input-number v-model="recEndDayOfMonth" class="full-w" :min="1" :max="31" :precision="0" />
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item label="End Time" has-feedback>
                    <a-time-picker
                      v-model="recEndTime"
                      class="full-w"
                      format="HH:mm"
                      placeholder="End time"
                      :minute-step="1"
                    />
                  </a-form-item>
                </a-col>
              </template>

              <a-col v-if="recurringPreviewVisible" :span="24" class="recurring-preview">
                <div class="recurring-preview__header mb-2">
                  <span class="fs-14 font-weight-bold">Preview</span>
                  <span class="text-muted fs-12 ml-2">
                    {{ estimatedRecurringOccurrences }} period(s), {{ selectedRowKeys.length }} URL(s) each
                    ({{ recurringWouldBeRows }} blackout row(s) on save)
                  </span>
                  <span v-if="recurringCapExceeded" class="text-danger fs-12 ml-2">
                    Exceeds limits ({{ MAX_RECURRING_PERIODS }} periods, {{ MAX_RECURRING_BLACKOUT_ROWS }} rows).
                    Narrow the range, change frequency, or remove URLs before saving.
                  </span>
                </div>
                <div ref="previewTableWrapper" v-resize="previewElSizeChange">
                  <PaginationTable
                    :pagination="previewPagin"
                    :columns="BlackoutPeriodPreviewColumn"
                    :dataSource="recurringPreviewRows"
                    :tableSize="previewTableSize"
                  >
                    <div slot="urls" slot-scope="{ record }">
                      <template v-for="item in record.urls">
                        <a
                          v-if="item && item.id != null && item.url"
                          :key="item.id"
                          :href="item.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="url text-wrap"
                        >
                          {{ item.url }}
                        </a>
                      </template>
                    </div>
                    <template slot="start" slot-scope="{ record }">
                      <div>
                        <p class="mb-0">
                          <time>{{ record.start | dateString }}</time>
                          <br>
                          <time>{{ record.start | timeString }}</time>
                        </p>
                      </div>
                    </template>
                    <template slot="end" slot-scope="{ record }">
                      <div>
                        <p class="mb-0">
                          <time>{{ record.end | dateString }}</time>
                          <br>
                          <time>{{ record.end | timeString }}</time>
                        </p>
                      </div>
                    </template>
                  </PaginationTable>
                </div>
                <div class="mt-2">
                  <PaginationBar
                    storageKey="blackoutPreviewPageSize"
                    :pageSize.sync="previewPagin.pageSize"
                    :total="previewPagin.total"
                    @change="previewPaginChange"
                  />
                </div>
              </a-col>
            </template>
          </a-row>
        </transition>
      </div>
      <div class="a-footer">
        <a-button key="back" size="large" class="mr-3 slide-down-enter-active">
          <router-link :to="'/blackout-periods'">
            Cancel
          </router-link>
        </a-button>
        <a-button type="primary" size="large" html-type="submit" class="slide-down-enter-active">
          Save
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import moment from 'moment-timezone';
  import { UserDate } from '@shared/services/userDate.service';
  import { HEALTH_STATUS } from '@constants/statuses';
  import { DATE_FORMAT } from '@constants/dateFormat';

  import BlackoutPeriodService from '../services/blackout-period.service';
  import {
    BlackoutModes,
    BlackoutFrequencies,
    DaysOfWeek,
    BLACKOUT_MODE,
    BLACKOUT_FREQUENCY,
    MAX_RECURRING_BLACKOUT_ROWS,
    MAX_RECURRING_PERIODS,
    dailySpanMinutes,
    weeklySpanMinutes,
    monthlySpanMinutes,
    BlackoutUrlStatusFilterOptions,
    DEFAULT_BLACKOUT_URL_STATUS_FILTERS,
    getBlackoutStatusTagClass,
  } from '../consts/BlackoutPeriod.consts';
  import { expandRecurrence, periodsToApiPayload } from '../helpers/recurrence-expand';
  import {
    buildUrlBlackoutStatusByUrlId,
    urlMatchesBlackoutStatusFilter,
  } from '../helpers/url-blackout-status';
  import { BlackoutPeriodPreviewColumn } from '../columnsTable/BlackoutPeriodPreviewColumn';

  export default {
    name: 'BlackoutPeriodForm',
    props: {
      urlIds: {
        type: Array,
        default: () => [],
      },
      startDate: {
        type: String,
        default: () => '',
      },
      endDate: {
        type: String,
        default: () => '',
      },
      disableRepeating: {
        type: Boolean,
        default: false,
      }
    },
    data() {
      let pageSize = Number(window.localStorage.getItem('blackoutUrlPageSize'));
      if (!pageSize) pageSize = 10;

      let previewPageSize = Number(window.localStorage.getItem('blackoutPreviewPageSize'));
      if (!previewPageSize) previewPageSize = 10;

      return {
        DATE_FORMAT,
        BlackoutModes,
        BlackoutFrequencies,
        DaysOfWeek,
        BLACKOUT_MODE,
        BLACKOUT_FREQUENCY,
        MAX_RECURRING_BLACKOUT_ROWS,
        MAX_RECURRING_PERIODS,
        BlackoutPeriodPreviewColumn,
        mode: BLACKOUT_MODE.ONCE,
        frequency: BLACKOUT_FREQUENCY.DAILY,
        effectiveFrom: null,
        effectiveTo: null,
        recStartDayOfWeek: 1,
        recEndDayOfWeek: 1,
        recStartDayOfMonth: 1,
        recEndDayOfMonth: 1,
        recStartTime: null,
        recEndTime: null,
        isLoading: true,
        isLoadingUrls: false,
        isLoadingBlackoutStatuses: false,
        isBlackoutStatusMapReady: false,
        urlBlackoutStatusByUrlId: {},
        isShowing404Urls: false,
        data: [],
        selectedRowKeys: [],
        BlackoutUrlStatusFilterOptions,
        filterParams: {
          search: '',
          blackoutStatuses: [...DEFAULT_BLACKOUT_URL_STATUS_FILTERS],
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0
        },
        previewPagin: {
          current: 1,
          pageSize: previewPageSize,
          total: 0
        },
        tableSize: {
          height: '',
          width: ''
        },
        previewTableDimensions: {
          height: '',
          width: ''
        },
        isShowBatchSelectUrlsModal: false,
        batchSelectUrls: '',
        showAllTags: false,
        visibleTagCount: 10,
        urlListCacheAll: null,
        urlListCache404: null,
        urlColumns: [
          {
            title: '#',
            dataIndex: 'index',
            sorter: (a, b) => a.index - b.index,
            width: 60,
            align: 'center'
          },
          {
            title: 'URL',
            dataIndex: 'title',
            slot: 'url',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
            ellipsis: true,
            width: 600
          }
        ]
      };
    },
    computed: {
      isAddMode() {
        return !this.startDate && !this.endDate;
      },
      isBlackoutStatusFilterActive() {
        const selected = this.filterParams.blackoutStatuses || [];
        return (
          selected.length > 0
          && selected.length < DEFAULT_BLACKOUT_URL_STATUS_FILTERS.length
        );
      },
      isHasSelected() {
        return this.selectedRowKeys.length > 0;
      },
      urlTableSize() {
        const rowHeight = 44;
        const headerHeight = 44;
        const emptyRowCount = 1;
        const current = Math.max(1, Number(this.pagin.current) || 1);
        const pageSize = Math.max(1, Number(this.pagin.pageSize) || 1);
        const start = pageSize * (current - 1);
        const remainingRows = Math.max(0, this.filteredData.length - start);
        const visibleRows = remainingRows > 0 ? Math.min(pageSize, remainingRows) : emptyRowCount;
        const contentHeight = headerHeight + (visibleRows * rowHeight);

        return {
          ...this.tableSize,
          height: contentHeight
        };
      },
      /**
       * ---> data (all URLs) + filterParams.search > [filteredData] > PaginationTable dataSource
       */
      filteredData() {
        const searchTerm = this.filterParams.search.trim().toLowerCase();
        let filtered = this.data;

        if (searchTerm) {
          filtered = filtered.filter((item) => {
            return (item.title || '').toLowerCase().includes(searchTerm);
          });
        }

        if (this.isAddMode && this.isBlackoutStatusMapReady) {
          filtered = filtered.filter((item) => urlMatchesBlackoutStatusFilter(
            item.id,
            this.urlBlackoutStatusByUrlId,
            this.filterParams.blackoutStatuses
          ));
        }

        const selectedSet = new Set(this.selectedRowKeys.map((k) => this.normalizeUrlRowKey(k)));
        return filtered.map((item, idx) => ({
          ...item,
          index: idx + 1,
          isChecked: selectedSet.has(item.id)
        }));
      },
      selectionSummaryMessage() {
        const totalSel = this.selectedRowKeys.length;
        const listed = this.filteredData.length;
        const visibleIds = new Set(this.filteredData.map((r) => r.id));
        const selectedShown = this.selectedRowKeys.filter((id) => visibleIds.has(this.normalizeUrlRowKey(id))).length;
        if (totalSel === selectedShown) {
          return `${totalSel} URL(s) selected out of ${listed}`;
        }
        return `${totalSel} URL(s) selected · ${selectedShown} selections are shown currently (clear search or change filters to see the rest in the table)`;
      },
      isStatusCodeMonitoringFeatureEnabled() {
        return this.$store.getters.hasFeature('status-code-monitoring');
      },
      expandedRecurringPeriods() {
        if (this.mode !== BLACKOUT_MODE.REPEATING || !this.effectiveFrom || !this.effectiveTo) {
          return [];
        }
        if (!this.recStartTime || !this.recEndTime) return [];
        try {
          return expandRecurrence(this.buildRecurrenceRule());
        } catch {
          return [];
        }
      },
      estimatedRecurringOccurrences() {
        return this.expandedRecurringPeriods.length;
      },
      recurringWouldBeRows() {
        return this.expandedRecurringPeriods.length * Math.max(0, this.selectedRowKeys.length);
      },
      recurringCapExceeded() {
        return (
          this.recurringWouldBeRows > MAX_RECURRING_BLACKOUT_ROWS
          || this.estimatedRecurringOccurrences > MAX_RECURRING_PERIODS
        );
      },
      recurringPreviewVisible() {
        return (
          this.mode === BLACKOUT_MODE.REPEATING
          && this.effectiveFrom
          && this.effectiveTo
          && this.recStartTime
          && this.recEndTime
          && this.selectedRowKeys.length > 0
          && this.estimatedRecurringOccurrences > 0
        );
      },
      recurringPreviewRows() {
        if (!this.recurringPreviewVisible) {
          return [];
        }
        const urls = this.selectedRowKeys
          .map((urlId) => {
            const id = this.normalizeUrlRowKey(urlId);
            const url = this.getUrlByKey(urlId);
            if (id == null || !url) {
              return null;
            }
            return { id, url };
          })
          .filter(Boolean);
        return this.expandedRecurringPeriods.map(({ start, end }, index) => ({
          id: `preview-${index}-${start.valueOf()}-${end.valueOf()}`,
          start: start.clone().toISOString(),
          end: end.clone().toISOString(),
          url_ids: urls.map((u) => u.id),
          urls: [...urls],
        }));
      },
      previewTableSize() {
        const rowHeight = 44;
        const headerHeight = 44;
        const emptyRowCount = 1;
        const current = Math.max(1, Number(this.previewPagin.current) || 1);
        const pageSize = Math.max(1, Number(this.previewPagin.pageSize) || 1);
        const total = this.recurringPreviewRows.length;
        const start = pageSize * (current - 1);
        const remainingRows = Math.max(0, total - start);
        const visibleRows = remainingRows > 0 ? Math.min(pageSize, remainingRows) : emptyRowCount;
        const contentHeight = headerHeight + (visibleRows * rowHeight);

        return {
          ...this.previewTableDimensions,
          height: contentHeight
        };
      },
    },
    watch: {
      mode(newMode) {
        if (newMode === BLACKOUT_MODE.ONCE) {
          this.resetRecurringScheduleFields();
        } else if (newMode === BLACKOUT_MODE.REPEATING) {
          this.resetOnceScheduleFields();
        }
      },
      filteredData: {
        immediate: false,
        handler(rows) {
          if (this.pagin.total !== rows.length) {
            this.pagin.total = rows.length;
          }
          const totalPages = Math.max(1, Math.ceil(rows.length / Math.max(1, this.pagin.pageSize)));
          if (this.pagin.current > totalPages) {
            this.pagin.current = totalPages;
          }
        }
      },
      recurringPreviewRows: {
        immediate: true,
        handler(rows) {
          if (this.previewPagin.total !== rows.length) {
            this.previewPagin.total = rows.length;
          }
          const totalPages = Math.max(1, Math.ceil(rows.length / Math.max(1, this.previewPagin.pageSize)));
          if (this.previewPagin.current > totalPages) {
            this.previewPagin.current = 1;
          }
        }
      }
    },
    beforeCreate() {
      this.blackoutPeriodForm = this.$form.createForm(this, { name: 'blackout-period-form' });
    },
    mounted() {
      this.fetchUrlsList();
      if (this.isAddMode) {
        this.loadUrlBlackoutStatuses();
      }
    },
    methods: {
      getBlackoutStatusTagClass,

      buildRecurrenceRule() {
        return {
          frequency: this.frequency,
          effectiveFrom: this.effectiveFrom.clone().format('YYYY-MM-DD'),
          effectiveTo: this.effectiveTo.clone().format('YYYY-MM-DD'),
          startTime: this.recStartTime.clone().format('HH:mm'),
          endTime: this.recEndTime.clone().format('HH:mm'),
          timezone: UserDate.userTimezone,
          startDayOfWeek: this.recStartDayOfWeek,
          endDayOfWeek: this.recEndDayOfWeek,
          startDayOfMonth: this.recStartDayOfMonth,
          endDayOfMonth: this.recEndDayOfMonth,
        };
      },

      resetRecurringScheduleFields() {
        this.effectiveFrom = null;
        this.effectiveTo = null;
        this.frequency = BLACKOUT_FREQUENCY.DAILY;
        this.recStartDayOfWeek = 1;
        this.recEndDayOfWeek = 1;
        this.recStartDayOfMonth = 1;
        this.recEndDayOfMonth = 1;
        this.recStartTime = null;
        this.recEndTime = null;
      },
      resetOnceScheduleFields() {
        this.$nextTick(() => {
          if (this.blackoutPeriodForm) {
            this.blackoutPeriodForm.setFieldsValue({ start: null, end: null });
          }
        });
      },

      range(start, end) {
        const result = [];
        for (let i = start; i < end; i++) {
          result.push(i);
        }
        return result;
      },
      getUrlByKey(key) {
        const id = this.normalizeUrlRowKey(key);
        const item = this.data.find((d) => d.id === id);
        return item ? item.title : key;
      },

      normalizeUrlRowKey(id) {
        if (id === null || id === undefined) {
          return id;
        }
        const n = Number(id);
        return Number.isFinite(n) ? n : id;
      },

      uniqueIds(ids) {
        const seen = {};
        const result = [];
        for (const id of ids) {
          const norm = this.normalizeUrlRowKey(id);
          const key = String(norm);
          if (seen[key]) {
            continue;
          }
          seen[key] = true;
          result.push(norm);
        }
        return result;
      },

      /**
       * ---> PaginationTable checkbox change > [onSelectChange] > merge selections across pages
       */
      onSelectChange(selectedIds) {
        const currentPageIdSet = new Set(this.filteredData
          .slice(this.pagin.pageSize * (this.pagin.current - 1), this.pagin.pageSize * this.pagin.current)
          .map((item) => item.id));

        const normalizedSelected = this.selectedRowKeys.map((k) => this.normalizeUrlRowKey(k));
        const otherPageSelections = normalizedSelected.filter(
          (key) => !currentPageIdSet.has(key)
        );
        this.selectedRowKeys = this.uniqueIds(otherPageSelections.concat(selectedIds));
      },

      selectAll() {
        const filteredIds = this.filteredData.map((item) => item.id);
        this.selectedRowKeys = this.uniqueIds(this.selectedRowKeys.concat(filteredIds));
      },

      deselectAll() {
        this.selectedRowKeys = [];
        this.showAllTags = false;
      },

      removeSelectedUrl(key) {
        const id = this.normalizeUrlRowKey(key);
        this.selectedRowKeys = this.selectedRowKeys.filter((k) => this.normalizeUrlRowKey(k) !== id);
      },

      /**
       * ---> Search input > [onSearch] > update filterParams.search + reset pagination
       */
      onSearch: debounce(function (event) {
        this.filterParams.search = (event.target.value && String(event.target.value).trim()) || '';
        this.pagin.current = 1;
        this.scrollUrlListToTop();
      }, 300),

      onBlackoutStatusFilterChange() {
        this.pagin.current = 1;
        this.scrollUrlListToTop();
      },

      async loadUrlBlackoutStatuses() {
        this.isLoadingBlackoutStatuses = true;
        this.isBlackoutStatusMapReady = false;
        try {
          const periods = await BlackoutPeriodService.getList();
          this.urlBlackoutStatusByUrlId = buildUrlBlackoutStatusByUrlId(periods);
        } catch {
          this.urlBlackoutStatusByUrlId = {};
          this.$showErrorMessage('Could not load blackout statuses for URL filtering.');
        } finally {
          this.isBlackoutStatusMapReady = true;
          this.isLoadingBlackoutStatuses = false;
        }
      },

      paginChange(pagin) {
        this.pagin = { ...pagin };
        this.scrollUrlListToTop();
      },

      previewPaginChange(pagin) {
        this.previewPagin = { ...pagin };
        this.scrollPreviewTableToTop();
      },

      elSizeChange(size) {
        this.tableSize = size;
      },

      previewElSizeChange(size) {
        this.previewTableDimensions = size;
      },

      scrollUrlListToTop() {
        this.$nextTick(() => {
          const wrapper = this.$refs.urlTableWrapper;
          if (wrapper) {
            wrapper.scrollTop = 0;
            const tableContainer = wrapper.querySelector('.table-container');
            if (tableContainer) {
              tableContainer.scrollTop = 0;
            }
          }
          const formBody = this.$el && this.$el.querySelector('.a-body');
          if (formBody) {
            formBody.scrollTop = 0;
          }
        });
      },

      scrollPreviewTableToTop() {
        this.$nextTick(() => {
          const wrapper = this.$refs.previewTableWrapper;
          if (!wrapper) return;
          wrapper.scrollTop = 0;
          const tableContainer = wrapper.querySelector('.table-container');
          if (tableContainer) {
            tableContainer.scrollTop = 0;
          }
        });
      },

      /**
       * ---> "Batch Select URLs" button > [showBatchSelectUrlsModal] > pre-populate textarea with selected URLs
       */
      showBatchSelectUrlsModal() {
        const selectedUrls = this.selectedRowKeys
          .map((key) => this.getUrlByKey(key))
          .filter((url) => url);
        this.batchSelectUrls = selectedUrls.join('\n');
        this.isShowBatchSelectUrlsModal = true;
      },

      closeBatchSelectUrlsModal() {
        this.isShowBatchSelectUrlsModal = false;
        this.batchSelectUrls = '';
      },

      handleChangeBatchSelectUrls(e) {
        this.batchSelectUrls = e.target.value;
      },

      /**
       * ---> Batch Select modal OK > [handleSubmitBatchSelectUrls] > parse URLs, match to data, update selectedRowKeys
       */
      handleSubmitBatchSelectUrls() {
        try {
          const urlLookup = {};
          this.data.forEach((item) => {
            if (item && item.title != null) {
              const key = item.title.trim();
              if (key && urlLookup[key] === undefined) {
                urlLookup[key] = item.id;
              }
            }
          });

          const pastedUrls = this.batchSelectUrls
            .split('\n')
            .map((url) => url.trim())
            .filter((url) => url.length > 0);

          const matchedIds = [];
          const notFound = [];

          pastedUrls.forEach((url) => {
            const id = urlLookup[url];
            if (id !== undefined) {
              matchedIds.push(id);
            } else {
              notFound.push(url);
            }
          });

          this.selectedRowKeys = this.uniqueIds(matchedIds);

          if (notFound.length > 0) {
            this.$showErrorMessage(`${notFound.length} URL(s) not found and were skipped.`);
          } else {
            this.$showSuccessMessage('Batch selected URLs successfully');
          }

          this.closeBatchSelectUrlsModal();
        } catch (err) {
          this.$showErrorMessage(err.message);
        }
      },

      disabledStartDate(current) {
        const endDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('end');
        return endDateAsMomentValue ? current.isAfter(endDateAsMomentValue) || current.isBefore(moment().startOf('day')) : current.isBefore(moment().startOf('day'));
      },
      disabledEndDate(current) {
        const startDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('start');
        return startDateAsMomentValue ? current.isBefore(startDateAsMomentValue) : current.isBefore(moment().startOf('day'));
      },
      disabledStartTime() {
        const nowTime = new Date();
        const startDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('start');
        return {
          disabledHours: () => (!startDateAsMomentValue || startDateAsMomentValue.isSame(moment(), 'day') ? this.range(0, nowTime.getHours()) : []),
          disabledMinutes: () => (!startDateAsMomentValue || startDateAsMomentValue.isSame(moment(), 'hour') ? this.range(0, nowTime.getMinutes()) : [])
        };
      },
      disabledEndTime() {
        const nowTime = new Date();
        const endDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('end');
        return {
          disabledHours: () => (!endDateAsMomentValue || endDateAsMomentValue.isSame(moment(), 'day') ? this.range(0, nowTime.getHours()) : []),
          disabledMinutes: () => (!endDateAsMomentValue || endDateAsMomentValue.isSame(moment(), 'hour') ? this.range(0, nowTime.getMinutes()) : [])
        };
      },
      disabledEffectiveFrom(current) {
        const today = moment().startOf('day');
        if (current && current.isBefore(today)) return true;
        if (this.effectiveTo) return current.isAfter(this.effectiveTo);
        return false;
      },
      disabledEffectiveTo(current) {
        const today = moment().startOf('day');
        if (current && current.isBefore(today)) return true;
        if (this.effectiveFrom) return current.isBefore(this.effectiveFrom);
        return false;
      },
      onChangeStartDate(value) {
        if (!value) {
          return;
        }
        const start = value.clone().seconds(0).milliseconds(0);
        const endDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('end');
        if (endDateAsMomentValue && start.isAfter(endDateAsMomentValue)) {
          this.blackoutPeriodForm.setFieldsValue({ end: null });
        }
      },
      onChangeEndDate(value) {
        if (!value) {
          return;
        }
        const end = value.clone().seconds(0).milliseconds(0);
        const startDateAsMomentValue = this.blackoutPeriodForm.getFieldValue('start');
        if (startDateAsMomentValue && end.isBefore(startDateAsMomentValue)) {
          this.blackoutPeriodForm.setFieldsValue({ start: null });
        }
      },
      
      async toggle404Urls() {
        if (this.isLoadingUrls || !this.isStatusCodeMonitoringFeatureEnabled) {
          return;
        }
        const next = !this.isShowing404Urls;
        this.isShowing404Urls = next;
        this.pagin.current = 1;
        try {
          await this.fetchUrlsList({ keepSelection: true });
        } catch (err) {
          this.isShowing404Urls = !next;
        }
      },

      formatRowsFromApiResponse(dataResponse) {
        const dataFiltered = (dataResponse || []).filter((item) => item.health_status !== HEALTH_STATUS.BASELINE_INCOMPLETE);
        const formatData = [];
        for (let index = 0, length = dataFiltered.length; index < length; index++) {
          const item = dataFiltered[index];
          formatData.push({
            key: item.id.toString(),
            id: item.id,
            title: item.url || item.url_name,
            httpStatusCode: item.status_code_monitoring && item.status_code_monitoring.http_status_code,
            isChecked: false
          });
        }
        return formatData;
      },

      applyUrlList(formatData, keepSelection) {
        const rows = formatData.map((r) => ({ ...r, isChecked: false }));
        this.data = rows;
        this.pagin.total = rows.length;
        this.scrollUrlListToTop();

        if (keepSelection) {
          const validIds = this.urlListCacheAll
            ? new Set(this.urlListCacheAll.map((item) => item.id))
            : new Set(rows.map((item) => item.id));
          this.selectedRowKeys = this.uniqueIds(
            this.selectedRowKeys.filter((id) => validIds.has(this.normalizeUrlRowKey(id)))
          );
        } else {
          const preselectedIds = [...this.urlIds].map((id) => Number(id));
          this.selectedRowKeys = preselectedIds.filter((id) => rows.some((d) => d.id === id));
        }

        this.setBlackoutPeriodForm();
      },

      async fetchUrlsList({ keepSelection = false, forceRefresh = false } = {}) {
        const want404 = this.isShowing404Urls && this.isStatusCodeMonitoringFeatureEnabled;

        if (!forceRefresh) {
          if (want404 && this.urlListCache404 !== null) {
            this.applyUrlList(this.urlListCache404, keepSelection);
            if (this.isLoading) {
              setTimeout(() => {
                this.isLoading = false;
              }, 300);
            }
            return;
          }
          if (!want404 && this.urlListCacheAll !== null) {
            this.applyUrlList(this.urlListCacheAll, keepSelection);
            if (this.isLoading) {
              setTimeout(() => {
                this.isLoading = false;
              }, 300);
            }
            return;
          }
        }

        this.isLoadingUrls = true;
        try {
          const dataResponse = await BlackoutPeriodService.getUrlList(
            want404 ? { statusCode: 404 } : {}
          );
          const formatData = this.formatRowsFromApiResponse(dataResponse);
          if (want404) {
            this.urlListCache404 = formatData.map((r) => ({ ...r }));
          } else {
            this.urlListCacheAll = formatData.map((r) => ({ ...r }));
          }
          this.applyUrlList(formatData, keepSelection);
        } catch {
          this.$showErrorMessage('HTTP error received from web server.!');
        } finally {
          this.isLoadingUrls = false;
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },

      setBlackoutPeriodForm() {
        this.blackoutPeriodForm.getFieldDecorator('start', {
          initialValue: this.startDate ? moment(this.startDate).tz(UserDate.userTimezone) : null,
          rules: [{ required: true, message: 'Please select Start Date!' }]
        });
        this.blackoutPeriodForm.getFieldDecorator('end', {
          initialValue: this.endDate ? moment(this.endDate).tz(UserDate.userTimezone) : null,
          rules: [{ required: true, message: 'Please select End Date!' }]
        });
      },

      /**
       * ---> Form submit > [onSubmit] > validate + emit { start, end, url_ids }
       */
      onSubmit(e) {
        e.preventDefault();
        if (!this.selectedRowKeys.length) {
          this.$showErrorMessage('Please select at least one Webpage!');
          return;
        }
        if (this.mode === BLACKOUT_MODE.REPEATING) {
          this.submitRepeating();
          return;
        }
        this.blackoutPeriodForm.validateFields((err, values) => {
            if (!err) {
              const startDate = values.start;
              const endDate = values.end;
              if (startDate.isAfter(endDate)) {
                this.$showErrorMessage('Please select start time before end time');
              } else {
                const bodyParams = {
                  start: moment(startDate.clone()).tz(UserDate.userTimezone, true).toString(),
                  end: moment(endDate.clone()).tz(UserDate.userTimezone, true).toString(),
                  url_ids: this.selectedRowKeys.map((id) => id.toString())
                };
                this.$emit('submit', bodyParams);
              }
            }
          });
      },

      /**
       * ---> Form submit (repeating mode) > [submitRepeating] > validate recurring fields + emit
       * `submit-recurring` with the recurrence rule. Backend expands it into N rows.
       */
      submitRepeating() {
        if (this.recurringCapExceeded) {
          this.$showErrorMessage(
            `This schedule exceeds the limit of ${MAX_RECURRING_BLACKOUT_ROWS} blackout rows or ${MAX_RECURRING_PERIODS} periods. Narrow the range, change frequency, or remove URLs.`
          );
          return;
        }
        if (!this.effectiveFrom || !this.effectiveTo) {
          this.$showErrorMessage('Please select an effective date range.');
          return;
        }
        if (this.effectiveFrom.isAfter(this.effectiveTo)) {
          this.$showErrorMessage('Effective From must be on or before Effective To.');
          return;
        }
        if (!this.recStartTime || !this.recEndTime) {
          this.$showErrorMessage('Please select both a start time and an end time.');
          return;
        }

        if (this.frequency === BLACKOUT_FREQUENCY.DAILY) {
          const span = dailySpanMinutes(this.recStartTime, this.recEndTime);
          if (span < 1) {
            this.$showErrorMessage('Daily blackout must be at least 1 minute long.');
            return;
          }
          if (span > 24 * 60) {
            this.$showErrorMessage('Daily blackout cannot exceed 24 hours.');
            return;
          }
        }

        if (this.frequency === BLACKOUT_FREQUENCY.WEEKLY) {
          if (this.recStartDayOfWeek == null || this.recEndDayOfWeek == null) {
            this.$showErrorMessage('Please select start and end days of the week.');
            return;
          }
          const span = weeklySpanMinutes(
            this.recStartDayOfWeek,
            this.recEndDayOfWeek,
            this.recStartTime,
            this.recEndTime
          );
          if (span < 1) {
            this.$showErrorMessage('Weekly blackout must be at least 1 minute long.');
            return;
          }
          if (span > 7 * 24 * 60) {
            this.$showErrorMessage('Weekly blackout cannot exceed 7 days.');
            return;
          }
        }

        if (this.frequency === BLACKOUT_FREQUENCY.MONTHLY) {
          if (!this.recStartDayOfMonth || this.recStartDayOfMonth < 1 || this.recStartDayOfMonth > 31
            || !this.recEndDayOfMonth || this.recEndDayOfMonth < 1 || this.recEndDayOfMonth > 31) {
            this.$showErrorMessage('Please enter start and end dates between 1 and 31.');
            return;
          }
          const span = monthlySpanMinutes(
            this.recStartDayOfMonth,
            this.recEndDayOfMonth,
            this.recStartTime,
            this.recEndTime
          );
          if (span < 1) {
            this.$showErrorMessage('Monthly blackout must be at least 1 minute long.');
            return;
          }
          if (span > 31 * 24 * 60) {
            this.$showErrorMessage('Monthly blackout cannot exceed 31 days.');
            return;
          }
        }

        const expanded = expandRecurrence(this.buildRecurrenceRule());
        if (!expanded.length) {
          this.$showErrorMessage('The recurrence rule produced no blackout periods in the selected range.');
          return;
        }
        if (expanded.length > MAX_RECURRING_PERIODS) {
          this.$showErrorMessage(`Too many periods (${expanded.length}). Maximum is ${MAX_RECURRING_PERIODS}.`);
          return;
        }

        const bodyParams = {
          url_ids: this.selectedRowKeys.map((id) => id.toString()),
          periods: periodsToApiPayload(expanded, UserDate.userTimezone),
        };

        this.$emit('submit-recurring', bodyParams);
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '../styles/blackout-status-tags';

  .recurring-preview {
    .url {
      display: block;
    }
  }

  .url-selection {
    &__status-hint {
      font-size: 11px;
      color: $prLgt;
      line-height: 1.3;
    }

    &__status-select {
      ::v-deep .ant-select-selection__choice {
        font-size: 12px;
      }
    }

    &__actions {
      .a-input-group {
        display: flex;
        align-items: center;
      }
    }

    &__toolbar-row {
      margin-top: 12px;
    }

    &__summary {
      .selected-tags-container {
        max-height: 120px;
        overflow-y: auto;
        padding: 8px 0;

        .ant-tag {
          margin-bottom: 4px;
          max-width: 350px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: middle;
        }
      }
    }
  }
</style>
